import { Reducer, Dispatch, useReducer, useMemo } from 'react';
import { RuleBuilderField, RuleBuilderInput } from '../rule-builder';
import { uniqueNumber } from '../../../util';
import { validateRulebuilderInput } from '../helpers';
import { SelectOption } from '@leasequery/lq.react-component-library';
import {
  COMPARISON_RULE_SELECT_OPTIONS,
  LOGICAL_RULE_SELECT_OPTIONS_ARRAY,
} from '../../../constants';

const cachedEditingRules = new Map<number, RuleBuilderInput>();
interface RuleBuilderState {
  fields: RuleBuilderField[];
}

type RuleBuilderAction =
  | {
      type: 'addField';
      payload: RuleBuilderField;
    }
  | {
      type: 'updateFields';
      payload: Array<RuleBuilderField>;
    }
  | {
      type: 'removeField';
      payload: RuleBuilderField;
    }
  | {
      type: 'clearFields';
      payload: RuleBuilderField;
    }
  | {
      type: 'addRuleToRuleBuilderField';
      payload: RuleBuilderField;
    }
  | {
      type: 'cancelFieldEditing';
      payload: RuleBuilderField;
    }
  | {
      type: 'saveEditingFields';
      payload: RuleBuilderField;
    }
  | {
      type: 'removeRule';
      payload: RuleBuilderInput;
    }
  | {
      type: 'updateRule';
      payload: RuleBuilderInput;
    }
  | {
      type: 'turnOnRuleEditingMode';
      payload: RuleBuilderInput;
    };

const initialState: RuleBuilderState = {
  fields: [],
};

const reducer: Reducer<RuleBuilderState, RuleBuilderAction> = (
  state,
  { type, payload },
): RuleBuilderState => {
  switch (type) {
    case 'addField':
      return {
        ...state,
        fields: [...state.fields, payload as RuleBuilderField],
      };
    case 'updateFields':
      return {
        ...state,
        fields: payload as Array<RuleBuilderField>,
      };
    case 'removeField':
      return {
        ...state,
        fields: state.fields.filter(
          (field) => field.fieldKey !== (payload as RuleBuilderField).fieldKey,
        ),
      };

    case 'clearFields': {
      const fields = state.fields.reduce((acc, field) => {
        if (!field.isIncludedByDefault) return acc;
        acc.push({
          ...field,
          rules: [],
        });

        return acc;
      }, [] as RuleBuilderField[]);

      return { ...state, fields };
    }
    case 'addRuleToRuleBuilderField': {
      const editingField = payload as RuleBuilderField;

      const comparisonRuleOptions =
        COMPARISON_RULE_SELECT_OPTIONS[editingField.fieldType];
      const comparisonRuleOptionsArray: SelectOption<string>[] = Object.values(
        comparisonRuleOptions,
      );

      const fields = state.fields.map((field) => {
        if (field.fieldKey !== editingField.fieldKey) {
          return field;
        }

        const rule: RuleBuilderInput = {
          fieldKey: editingField.fieldKey,
          id: -uniqueNumber(),
          isEditing: true,
          isNew: true,
          logicalRule: LOGICAL_RULE_SELECT_OPTIONS_ARRAY[1]
            .value as RuleBuilderInput['logicalRule'],
          comparisonRule: comparisonRuleOptionsArray[0]
            .value as RuleBuilderInput['comparisonRule'],
          value1Err: '',
          value2Err: '',
        };
        return {
          ...field,
          rules: [...editingField.rules, rule],
        };
      });

      return { ...state, fields };
    }

    case 'saveEditingFields': {
      const editingField = { ...payload } as RuleBuilderField;
      const fields = state.fields.map((field) => {
        if (field.fieldKey !== editingField.fieldKey) {
          return field;
        }

        const nextField = { ...field };
        let hasError = false;

        nextField.rules = nextField.rules.map((rule) => {
          if (!rule.isEditing) {
            return rule;
          }

          const nextRule = validateRulebuilderInput(nextField, rule);

          nextRule.isEditing =
            nextRule.value1Err !== '' || nextRule.value2Err !== '';
          nextRule.isSaved = nextRule.isSaved || !nextRule.isEditing;

          hasError = hasError || nextRule.isEditing;
          return nextRule;
        });

        nextField.err = hasError ? 'Must use valid field content' : '';

        return nextField;
      });

      return { ...state, fields };
    }

    case 'cancelFieldEditing': {
      const editingField = payload as RuleBuilderField;
      const fields = state.fields.map((field) => {
        if (field.fieldKey !== editingField.fieldKey) {
          return field;
        }
        const nextField = { ...field, err: '' };
        nextField.rules = nextField.rules.reduce((acc, rule) => {
          if (!rule.isSaved) return acc;

          // Should restore the origin information of the editing rule when users cancel editing
          // Let get it from cache then clear the cache since it don't need anymore
          const cachedRule = cachedEditingRules.get(rule.id);
          cachedEditingRules.delete(rule.id);

          // If the saved rule is not in edting mode, the cache will be empty
          // Let use it's state information
          const nextRule = cachedRule || rule;

          nextRule.isEditing = false;
          acc.push(nextRule);

          return acc;
        }, []);

        return nextField;
      });

      return { ...state, fields };
    }

    case 'removeRule': {
      const editingRule = payload as RuleBuilderInput;
      const fields = state.fields.map((field) => {
        if (field.fieldKey !== editingRule.fieldKey) {
          return field;
        }
        const nextField = { ...field };
        nextField.rules = nextField.rules.filter(
          (rule) => rule.id !== editingRule.id,
        );

        return nextField;
      });

      return { ...state, fields };
    }

    case 'updateRule': {
      const editingRule = payload as RuleBuilderInput;
      const fields = state.fields.map((field) => {
        if (field.fieldKey !== editingRule.fieldKey) {
          return field;
        }
        const nextField = { ...field };
        nextField.rules = nextField.rules.map((rule) => {
          if (rule.id !== editingRule.id) {
            return rule;
          }
          return editingRule;
        });

        return nextField;
      });

      return { ...state, fields };
    }

    case 'turnOnRuleEditingMode': {
      const editingRule = payload as RuleBuilderInput;
      const fields = state.fields.map((field) => {
        if (field.fieldKey !== editingRule.fieldKey) {
          return field;
        }

        const nextField = { ...field };
        nextField.rules = nextField.rules.map((rule) => {
          if (rule.id !== editingRule.id) {
            return rule;
          }

          // Need to cache the last infomation of editing rule
          //   so that we can restore it when users cancel editing
          cachedEditingRules.set(rule.id, rule);
          // then set this rule to editing mode
          return { ...rule, isEditing: true };
        });

        return nextField;
      });

      return { ...state, fields };
    }

    default:
      throw new Error('unhandle action');
  }
};

interface RuleBuilderDispatchers {
  addRuleBuilderField: (payload: RuleBuilderField) => void;
  updateRuleBuilderFields: (payload: Array<RuleBuilderField>) => void;
  removeRuleBuilderField: (payload: RuleBuilderField) => void;
  clearRuleBuilderFields: () => void;
  addRuleToRuleBuilderField: (payload: RuleBuilderField) => void;
  cancelFieldEditing: (payload: RuleBuilderField) => void;
  saveEditingFields: (payload: RuleBuilderField) => void;
  removeRule: (payload: RuleBuilderInput) => void;
  updateRule: (payload: RuleBuilderInput) => void;
  turnOnRuleEditingMode: (payload: RuleBuilderInput) => void;
}

const buildDispatchers = (
  dispatch: Dispatch<RuleBuilderAction>,
): RuleBuilderDispatchers => ({
  addRuleBuilderField: (payload: RuleBuilderField): void =>
    dispatch({
      type: 'addField',
      payload,
    }),
  updateRuleBuilderFields: (payload: Array<RuleBuilderField>): void =>
    dispatch({
      type: 'updateFields',
      payload,
    }),
  removeRuleBuilderField: (payload: RuleBuilderField): void =>
    dispatch({
      type: 'removeField',
      payload,
    }),
  clearRuleBuilderFields: (): void =>
    dispatch({
      type: 'clearFields',
      payload: null,
    }),
  addRuleToRuleBuilderField: (payload: RuleBuilderField): void =>
    dispatch({
      type: 'addRuleToRuleBuilderField',
      payload,
    }),
  cancelFieldEditing: (payload: RuleBuilderField): void =>
    dispatch({
      type: 'cancelFieldEditing',
      payload,
    }),
  saveEditingFields: (payload: RuleBuilderField): void =>
    dispatch({
      type: 'saveEditingFields',
      payload,
    }),
  removeRule: (payload: RuleBuilderInput): void =>
    dispatch({
      type: 'removeRule',
      payload,
    }),
  updateRule: (payload: RuleBuilderInput): void =>
    dispatch({
      type: 'updateRule',
      payload,
    }),
  turnOnRuleEditingMode: (payload: RuleBuilderInput): void =>
    dispatch({
      type: 'turnOnRuleEditingMode',
      payload,
    }),
});

function useRuleBuilderReducer(): [RuleBuilderState, RuleBuilderDispatchers] {
  const [state, dispatch] = useReducer(reducer, initialState);

  const dispatchers = useMemo(() => buildDispatchers(dispatch), []);

  return [state, dispatchers];
}

export { useRuleBuilderReducer };
